import { ActionResultScreen } from '@acme/features';
import type { BreadcrumbItem } from '@acme/foundation';
import {
  $application,
  $isLoading,
  applicationOpened,
  applicationReset,
} from '@entities/application';
import {
  $actionResult,
  actionResultReset,
} from '@widgets/applicationActions/model';
import { useUnit } from 'effector-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useLocation,
  useNavigate,
  useOutlet,
  useParams,
} from 'react-router-dom';
import { ApplicationPageWrapper } from './wrapper';

type SetBreadcrumbItems = (items: BreadcrumbItem[]) => void;

const ApplicationLayoutContext = createContext<SetBreadcrumbItems | null>(null);

export const useSetApplicationBreadcrumbs = (items: BreadcrumbItem[]) => {
  const setBreadcrumbItems = useContext(ApplicationLayoutContext);
  useEffect(() => {
    setBreadcrumbItems?.(items);
  }, [items, setBreadcrumbItems]);
};

export const ApplicationLayout = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const outlet = useOutlet();

  const [breadcrumbItems, setBreadcrumbItems] = useState<BreadcrumbItem[]>([]);

  const {
    application,
    isLoading,
    onOpened,
    onReset,
    actionResult,
    onActionResultReset,
  } = useUnit({
    application: $application,
    isLoading: $isLoading,
    onOpened: applicationOpened,
    onReset: applicationReset,
    actionResult: $actionResult,
    onActionResultReset: actionResultReset,
  });

  const loadedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!id) return;
    if (loadedIdRef.current === id) return;
    loadedIdRef.current = id;
    onOpened({ id });
    return () => {
      onReset();
      onActionResultReset();
      const prevId = id;
      queueMicrotask(() => {
        if (loadedIdRef.current === prevId) loadedIdRef.current = null;
      });
    };
  }, [id, onOpened, onReset, onActionResultReset]);

  const setBreadcrumbItemsStable = useCallback((items: BreadcrumbItem[]) => {
    setBreadcrumbItems(items);
  }, []);

  const contextValue = useMemo(
    () => setBreadcrumbItemsStable,
    [setBreadcrumbItemsStable]
  );

  const handleBackFromResult = useCallback(() => {
    onActionResultReset();
    if (actionResult?.customResult?.link) {
      navigate(actionResult.customResult.link);
    } else if (id) {
      onOpened({ id });
    }
  }, [onActionResultReset, onOpened, id, actionResult?.customResult?.link, navigate]);

  const isIndexRoute =
    pathname === `/application/${id}` || pathname === `/application/${id}/`;

  if (actionResult) {
    return (
      <ApplicationLayoutContext.Provider value={contextValue}>
        <ApplicationPageWrapper
          breadcrumbItems={breadcrumbItems}
          isLoading={false}
          isFound
        >
          <ActionResultScreen
            isError={actionResult.isError}
            applicationCode={application?.code}
            actionName={actionResult.actionName}
            customResult={actionResult.customResult}
            onBack={handleBackFromResult}
          />
        </ApplicationPageWrapper>
      </ApplicationLayoutContext.Provider>
    );
  }

  if (isIndexRoute) {
    return (
      <ApplicationLayoutContext.Provider value={contextValue}>
        {outlet}
      </ApplicationLayoutContext.Provider>
    );
  }

  return (
    <ApplicationLayoutContext.Provider value={contextValue}>
      <ApplicationPageWrapper
        breadcrumbItems={breadcrumbItems}
        isLoading={isLoading}
        isFound={!!application}
      >
        {outlet}
      </ApplicationPageWrapper>
    </ApplicationLayoutContext.Provider>
  );
};
